import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Power as PowerIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Build as BuildIcon,
  Info as InfoIcon,
  Lightbulb as LightbulbIcon,
  Lock as LockIcon,
  Thermostat as ThermostatIcon,
  Devices as DevicesIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`iot-tabpanel-${index}`}
      aria-labelledby={`iot-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const IoTManagerDashboard = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hotelInfo, setHotelInfo] = useState(null);
  const [devices, setDevices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [openDeviceDialog, setOpenDeviceDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [filterRoom, setFilterRoom] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const [deviceForm, setDeviceForm] = useState({
    device_name: '',
    device_type: '',
    category: '',
    room_id: '',
    manufacturer: '',
    model: '',
    location_description: '',
    configuration: {}
  });

  useEffect(() => {
    loadAllData();
    const interval = setInterval(() => {
      refreshStatistics();
      refreshMaintenanceAlerts();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadHotelInfo(),
        loadDevices(),
        loadRooms(),
        loadStatistics(),
        loadMaintenanceAlerts()
      ]);
    } catch (error) {
      showNotification('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadHotelInfo = async () => {
    try {
      const response = await api.get('/api/iot-manager/hotel-info');
      if (response.data.success) {
        setHotelInfo(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load hotel info:', error);
    }
  };

  const loadDevices = async () => {
    try {
      const params = new URLSearchParams();
      if (filterRoom) params.append('roomId', filterRoom);
      if (filterCategory) params.append('category', filterCategory);
      if (filterStatus) params.append('status', filterStatus);

      const response = await api.get(`/api/iot-manager/devices?${params.toString()}`);
      if (response.data.success) {
        setDevices(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };

  const loadRooms = async () => {
    try {
      const response = await api.get('/api/iot-manager/rooms');
      if (response.data.success) {
        setRooms(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/api/iot-manager/statistics');
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const loadMaintenanceAlerts = async () => {
    try {
      const response = await api.get('/api/iot-manager/maintenance-alerts');
      if (response.data.success) {
        setMaintenanceAlerts(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load maintenance alerts:', error);
    }
  };

  const refreshStatistics = async () => {
    await loadStatistics();
  };

  const refreshMaintenanceAlerts = async () => {
    await loadMaintenanceAlerts();
  };

  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  const handleAddDevice = () => {
    setEditingDevice(null);
    setDeviceForm({
      device_name: '',
      device_type: '',
      category: '',
      room_id: '',
      manufacturer: '',
      model: '',
      location_description: '',
      configuration: {}
    });
    setOpenDeviceDialog(true);
  };

  const handleEditDevice = (device) => {
    setEditingDevice(device);
    setDeviceForm({
      device_name: device.device_name,
      device_type: device.device_type,
      category: device.category,
      room_id: device.room_id || '',
      manufacturer: device.manufacturer || '',
      model: device.model || '',
      location_description: device.location_description || '',
      configuration: device.configuration || {}
    });
    setOpenDeviceDialog(true);
  };

  const handleSaveDevice = async () => {
    try {
      if (editingDevice) {
        const response = await api.put(`/api/iot-manager/devices/${editingDevice.device_id}`, deviceForm);
        if (response.data.success) {
          showNotification('Device updated successfully', 'success');
          loadDevices();
          loadStatistics();
        }
      } else {
        const response = await api.post('/api/iot-manager/devices', deviceForm);
        if (response.data.success) {
          showNotification('Device added successfully', 'success');
          loadDevices();
          loadStatistics();
        }
      }
      setOpenDeviceDialog(false);
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save device', 'error');
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to delete this device?')) return;

    try {
      const response = await api.delete(`/api/iot-manager/devices/${deviceId}`);
      if (response.data.success) {
        showNotification('Device deleted successfully', 'success');
        loadDevices();
        loadStatistics();
      }
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to delete device', 'error');
    }
  };

  const handleSyncDevice = async (deviceId) => {
    try {
      const response = await api.post(`/api/iot-manager/devices/${deviceId}/sync`);
      if (response.data.success) {
        showNotification('Device synced successfully', 'success');
        loadDevices();
      }
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to sync device', 'error');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon color="success" />;
      case 'offline':
        return <ErrorIcon color="disabled" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'maintenance':
        return <BuildIcon color="warning" />;
      default:
        return <InfoIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'default';
      case 'error': return 'error';
      case 'maintenance': return 'warning';
      default: return 'default';
    }
  };

  const getAlertColor = (priority) => {
    switch (priority) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'lighting':
        return <LightbulbIcon />;
      case 'security':
        return <LockIcon />;
      case 'climate':
        return <ThermostatIcon />;
      default:
        return <DevicesIcon />;
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hotel Information Header */}
      {hotelInfo && (
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Typography variant="h4" gutterBottom>
              {hotelInfo.name}
            </Typography>
            <Typography variant="body1">
              {hotelInfo.address} • {hotelInfo.city}, {hotelInfo.country}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Devices
                </Typography>
                <Typography variant="h3">
                  {statistics.summary.total_devices}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.light' }}>
              <CardContent>
                <Typography color="white" gutterBottom>
                  Online
                </Typography>
                <Typography variant="h3" color="white">
                  {statistics.summary.online_devices}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.light' }}>
              <CardContent>
                <Typography color="white" gutterBottom>
                  Offline
                </Typography>
                <Typography variant="h3" color="white">
                  {statistics.summary.offline_devices}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'error.light' }}>
              <CardContent>
                <Typography color="white" gutterBottom>
                  Errors
                </Typography>
                <Typography variant="h3" color="white">
                  {statistics.summary.error_devices}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Card>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab 
            label="Devices" 
            icon={<DevicesIcon />} 
            iconPosition="start"
          />
          <Tab 
            label={
              <Badge badgeContent={maintenanceAlerts.length} color="error">
                Maintenance
              </Badge>
            }
            icon={<BuildIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Rooms" 
            icon={<InfoIcon />}
            iconPosition="start"
          />
        </Tabs>

        {/* Devices Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter by Room</InputLabel>
              <Select
                value={filterRoom}
                label="Filter by Room"
                onChange={(e) => {
                  setFilterRoom(e.target.value);
                  setTimeout(loadDevices, 100);
                }}
              >
                <MenuItem value="">All Rooms</MenuItem>
                {rooms.map((room) => (
                  <MenuItem key={room.room_id} value={room.room_id}>
                    {room.room_number}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={filterCategory}
                label="Filter by Category"
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setTimeout(loadDevices, 100);
                }}
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="lighting">Lighting</MenuItem>
                <MenuItem value="security">Security</MenuItem>
                <MenuItem value="climate">Climate</MenuItem>
                <MenuItem value="entertainment">Entertainment</MenuItem>
                <MenuItem value="sensor">Sensor</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={filterStatus}
                label="Filter by Status"
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setTimeout(loadDevices, 100);
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadDevices}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddDevice}
            >
              Add Device
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Device</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Battery</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.device_id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getCategoryIcon(device.category)}
                        <div>
                          <Typography variant="body2" fontWeight="bold">
                            {device.device_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {device.manufacturer} {device.model}
                          </Typography>
                        </div>
                      </Box>
                    </TableCell>
                    <TableCell>{device.room_number || 'Unassigned'}</TableCell>
                    <TableCell>
                      <Chip label={device.category} size="small" />
                    </TableCell>
                    <TableCell>{device.device_type}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(device.status)}
                        label={device.status}
                        color={getStatusColor(device.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {device.battery_level ? `${device.battery_level}%` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {device.last_seen
                        ? new Date(device.last_seen).toLocaleString()
                        : 'Never'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Sync Status">
                        <IconButton
                          size="small"
                          onClick={() => handleSyncDevice(device.device_id)}
                          disabled={!device.device_identifier}
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditDevice(device)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteDevice(device.device_id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Maintenance Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Maintenance Alerts ({maintenanceAlerts.length})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadMaintenanceAlerts}
            >
              Refresh
            </Button>
          </Box>

          {maintenanceAlerts.length === 0 ? (
            <Alert severity="success">
              All devices are functioning normally. No maintenance required.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Priority</TableCell>
                    <TableCell>Alert Type</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Room</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Battery</TableCell>
                    <TableCell>Last Seen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {maintenanceAlerts.map((alert) => (
                    <TableRow key={alert.device_id}>
                      <TableCell>
                        <Chip
                          label={alert.priority.toUpperCase()}
                          color={getAlertColor(alert.priority)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WarningIcon color={getAlertColor(alert.priority)} />
                          {alert.alert_type}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {alert.device_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {alert.device_type}
                        </Typography>
                      </TableCell>
                      <TableCell>{alert.room_number || 'Unassigned'}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(alert.status)}
                          label={alert.status}
                          color={getStatusColor(alert.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {alert.battery_level ? (
                          <span style={{ color: alert.battery_level < 20 ? 'red' : 'inherit' }}>
                            {alert.battery_level}%
                          </span>
                        ) : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {alert.last_seen
                          ? new Date(alert.last_seen).toLocaleString()
                          : 'Never'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Rooms Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">Hotel Rooms Overview</Typography>
          </Box>

          <Grid container spacing={2}>
            {rooms.map((room) => (
              <Grid item xs={12} sm={6} md={4} key={room.room_id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Room {room.room_number}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {room.room_type} • Floor {room.floor}
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Chip
                        label={`${room.device_count || 0} devices`}
                        size="small"
                        icon={<DevicesIcon />}
                      />
                      <Chip
                        label={`${room.online_devices || 0} online`}
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon />}
                      />
                      {parseInt(room.error_devices) > 0 && (
                        <Chip
                          label={`${room.error_devices} errors`}
                          size="small"
                          color="error"
                          icon={<ErrorIcon />}
                        />
                      )}
                    </Box>
                    <Chip
                      label={room.status}
                      size="small"
                      color={room.status === 'available' ? 'success' : 'default'}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </Card>

      {/* Add/Edit Device Dialog */}
      <Dialog
        open={openDeviceDialog}
        onClose={() => setOpenDeviceDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingDevice ? 'Edit Device' : 'Add New Device'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Device Name"
              value={deviceForm.device_name}
              onChange={(e) => setDeviceForm({ ...deviceForm, device_name: e.target.value })}
              required
              fullWidth
            />

            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={deviceForm.category}
                label="Category"
                onChange={(e) => setDeviceForm({ ...deviceForm, category: e.target.value })}
              >
                <MenuItem value="lighting">Lighting</MenuItem>
                <MenuItem value="security">Security</MenuItem>
                <MenuItem value="climate">Climate</MenuItem>
                <MenuItem value="entertainment">Entertainment</MenuItem>
                <MenuItem value="sensor">Sensor</MenuItem>
                <MenuItem value="appliance">Appliance</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Device Type"
              value={deviceForm.device_type}
              onChange={(e) => setDeviceForm({ ...deviceForm, device_type: e.target.value })}
              placeholder="e.g., Smart Light, Door Lock, Thermostat"
              required
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Assign to Room</InputLabel>
              <Select
                value={deviceForm.room_id}
                label="Assign to Room"
                onChange={(e) => setDeviceForm({ ...deviceForm, room_id: e.target.value })}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {rooms.map((room) => (
                  <MenuItem key={room.room_id} value={room.room_id}>
                    Room {room.room_number} ({room.room_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Manufacturer"
                  value={deviceForm.manufacturer}
                  onChange={(e) => setDeviceForm({ ...deviceForm, manufacturer: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Model"
                  value={deviceForm.model}
                  onChange={(e) => setDeviceForm({ ...deviceForm, model: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <TextField
              label="Location Description"
              value={deviceForm.location_description}
              onChange={(e) => setDeviceForm({ ...deviceForm, location_description: e.target.value })}
              multiline
              rows={2}
              placeholder="e.g., Main ceiling light, Entrance door, Near window"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeviceDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveDevice}
            variant="contained"
            disabled={!deviceForm.device_name || !deviceForm.device_type || !deviceForm.category}
          >
            {editingDevice ? 'Update' : 'Add'} Device
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      {notification.open && (
        <Alert
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
          sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }}
        >
          {notification.message}
        </Alert>
      )}
    </Container>
  );
};

export default IoTManagerDashboard;
